/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomMultiSelectV2.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InfoButton from "../../../infoButton/InfoButton";

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },

  chip: {
    backgroundColor: "#53565A",
    color: "white",
    borderRadius: 0,
    "& .MuiChip-deleteIcon": {
      color: "white",
      fontSize: 18,
      "&:hover": {
        color: "white",
      },
    },
  },
};

export default function CustomMultiSelectV2({
  selectedValues,
  setSelectedValues,
  optionsList,
  required,
  label,
  txtFldStyle = {},
  renderOption,
  getOptionLabel,
  infoname,
  height,
  placeholder = true,
  disabled = false,
}) {
  const theme = useTheme();

  const textFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "#d9d9d9", borderRadius: "0px" },
      ...(height && { minHeight: height }),
      "&:hover fieldset": { borderColor: "#8c8c8c" },
      "&.Mui-focused fieldset": { borderColor: "#000000" },
      "&.Mui-disabled fieldset": { borderColor: "#e0e0e0" },
      "&.Mui-error fieldset": { borderColor: "#e53935" },

      "&:hover .MuiInputBase-input::placeholder": { color: "#848587" },
      "&.Mui-focused .MuiInputBase-input::placeholder": { color: "#232323" },
    },

    "& .MuiInputLabel-root": {
      color: "#000",
      fontSize: "0.9rem",
      "&.Mui-error": { color: "#e53935" },
      "&.Mui-disabled": { color: "#bdbdbd" },
    },
    "& .MuiInputBase-input::placeholder": { color: "#999", opacity: 1 },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "#e53935",
      fontSize: "0.8rem",
      marginLeft: 0,
      marginTop: "4px",
    },
    ...txtFldStyle,
  };

  return (
    <Box sx={styles.container}>
      {/* Label */}
      <Box sx={{ display: "flex" }}>
        <Typography
          variant="subtitle1"
          sx={{ color: "#6f7174", fontSize: "0.9rem", fontWeight: 500 }}
        >
          {label} <span style={{ color: "red" }}>{required ? "*" : ""}</span>
        </Typography>
        <Box sx={{ width: 5, height: 5 }}>
          {infoname?.length > 0 ? (
            <InfoButton
              dimension={{ width: 15, height: 15 }}
              infoKey={infoname}
              color={"black"}
            />
          ) : null}
        </Box>
      </Box>

      {/* Autocomplete */}
      <Autocomplete
        size="small"
        multiple
        renderOption={renderOption}
        options={optionsList}
        value={selectedValues}
        onChange={(event, newValue) => {
          if (disabled) return;
          setSelectedValues(newValue);
        }}
        disabled={disabled}
        filterSelectedOptions={true}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, value) =>
          typeof option.value === "object"
            ? JSON.stringify(option.value) === JSON.stringify(value.value)
            : option.value === value.value
        }
        // 🔥 Scrollable tags area
        renderTags={(value, getTagProps) => (
          <Box
            sx={{
              maxHeight: 70, // chips container height
              overflowY: "auto", // scrolling enabled
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              paddingY: "4px",
              scrollbarWidth: "thin",
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": {
                background: "#9e9e9e",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb:hover": { background: "#6b6b6b" },
            }}
          >
            {value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.value}
                label={option.label}
                sx={styles.chip}
              />
            ))}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            {...(placeholder && { placeholder: `Search/Select` })}
          />
        )}
        sx={{ ...textFieldStyle }}
      />
    </Box>
  );
}
